import { supabase } from '../lib/supabase';

export const profitCalculatorService = {
  // Get tax settings
  async getTaxSettings() {
    const { data, error } = await supabase?.from('tax_settings')?.select('*')?.single();
    
    if (error) throw error;
    
    return {
      defaultDasPercentual: data?.default_das_percentual,
      defaultProlaboreMensal: data?.default_prolabore_mensal,
      defaultInssPercentual: data?.default_inss_percentual,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update tax settings
  async updateTaxSettings(settings) {
    const { data, error } = await supabase?.from('tax_settings')?.update({
        default_das_percentual: settings?.defaultDasPercentual,
        default_prolabore_mensal: settings?.defaultProlaboreMensal,
        default_inss_percentual: settings?.defaultInssPercentual
      })?.eq('id', (await this.getTaxSettings())?.id)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Get IRRF tax brackets
  async getIrrfBrackets() {
    const { data, error } = await supabase?.from('irrf_tax_brackets')?.select('*')?.eq('is_active', true)?.order('ordem', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(bracket => ({
      id: bracket?.id,
      limiteSuperior: bracket?.limite_superior,
      aliquota: bracket?.aliquota,
      parceladeduzir: bracket?.parcela_deduzir,
      ordem: bracket?.ordem,
      isActive: bracket?.is_active,
      createdAt: bracket?.created_at,
      updatedAt: bracket?.updated_at
    }));
  },

  // Create IRRF bracket
  async createIrrfBracket(bracket) {
    const { data, error } = await supabase?.from('irrf_tax_brackets')?.insert({
        limite_superior: bracket?.limiteSuperior,
        aliquota: bracket?.aliquota,
        parcela_deduzir: bracket?.parcelaDeduzir,
        ordem: bracket?.ordem,
        is_active: true
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Update IRRF bracket
  async updateIrrfBracket(id, bracket) {
    const { data, error } = await supabase?.from('irrf_tax_brackets')?.update({
        limite_superior: bracket?.limiteSuperior,
        aliquota: bracket?.aliquota,
        parcela_deduzir: bracket?.parcelaDeduzir,
        ordem: bracket?.ordem
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Delete IRRF bracket
  async deleteIrrfBracket(id) {
    const { error } = await supabase?.from('irrf_tax_brackets')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  // Calculate IRRF based on base value
  async calculateIrrf(baseIrrf) {
    try {
      // Ensure baseIrrf is a valid number
      if (!Number.isFinite(baseIrrf) || baseIrrf <= 0) {
        return 0;
      }
      
      const brackets = await this.getIrrfBrackets();
      
      // If no brackets configured, return 0
      if (!brackets || brackets?.length === 0) {
        console.warn('Nenhuma faixa de IRRF configurada. IRRF será 0.');
        return 0;
      }
      
      // Find applicable bracket
      const applicableBracket = brackets?.find(
        bracket => baseIrrf <= bracket?.limiteSuperior
      );
      
      if (!applicableBracket) {
        // If base exceeds all brackets, use the highest bracket
        const highestBracket = brackets?.[brackets?.length - 1];
        if (highestBracket && Number.isFinite(highestBracket?.aliquota) && Number.isFinite(highestBracket?.parceladeduzir)) {
          const irrf = Math.max(
            0,
            (baseIrrf * highestBracket?.aliquota / 100) - highestBracket?.parceladeduzir
          );
          return Number.isFinite(irrf) ? irrf : 0;
        }
        return 0;
      }
      
      // Ensure bracket values are valid numbers
      if (!Number.isFinite(applicableBracket?.aliquota) || !Number.isFinite(applicableBracket?.parceladeduzir)) {
        console.warn('Faixa de IRRF com valores inválidos:', applicableBracket);
        return 0;
      }
      
      const irrf = Math.max(
        0,
        (baseIrrf * applicableBracket?.aliquota / 100) - applicableBracket?.parceladeduzir
      );
      
      return Number.isFinite(irrf) ? irrf : 0;
    } catch (error) {
      console.error('Erro ao calcular IRRF:', error);
      return 0;
    }
  },

  // Perform complete profit calculation
  async performCalculation(params) {
    // Extract parameters
    const {
      totalObra,
      valorJaFaturado = 0,
      custoEmpreiteiro = 0,
      equipeMensalPrestadores = 0,
      visitasObraTotal = 0,
      outrosCustosDiretos = 0,
      fixosMensaisEscritorio = 0,
      duracaoSemanas,
      duracaoMeses,
      dasPercentualEfetivo,
      dasBaseCalculo,
      prolaboreMensal,
      inssPercentualSeg,
      nomeObra = '',
      observacoes = ''
    } = params;

    // Ensure all numeric values are valid
    const safeNumber = (val) => Number.isFinite(val) ? val : 0;

    // 1. Calculate revenue
    const valorAFaturar = safeNumber(totalObra) - safeNumber(valorJaFaturado);

    // 2. Calculate direct costs
    const custoDiretoTotal = safeNumber(custoEmpreiteiro) + safeNumber(visitasObraTotal) + safeNumber(outrosCustosDiretos);

    // 3. Calculate team costs
    const custoEquipeTotal = safeNumber(equipeMensalPrestadores) * safeNumber(duracaoMeses);

    // 4. Calculate fixed costs
    const custoFixosTotal = safeNumber(fixosMensaisEscritorio) * safeNumber(duracaoMeses);

    // 5. Calculate taxes
    const dasBase = dasBaseCalculo === 'valor_a_faturar' ? valorAFaturar : safeNumber(totalObra);
    const dasTotal = dasBase * (safeNumber(dasPercentualEfetivo) / 100);

    const inssMensal = safeNumber(prolaboreMensal) * (safeNumber(inssPercentualSeg) / 100);
    const baseIrrf = safeNumber(prolaboreMensal) - inssMensal;
    const irrfMensal = await this.calculateIrrf(baseIrrf);
    const darfTotal = (safeNumber(inssMensal) + safeNumber(irrfMensal)) * safeNumber(duracaoMeses);

    // 6. Calculate totals
    const totalGastos = custoDiretoTotal + custoEquipeTotal + custoFixosTotal + dasTotal + darfTotal;
    const lucroLiquido = safeNumber(totalObra) - totalGastos;
    const lucroPercent = safeNumber(totalObra) > 0 ? (lucroLiquido / safeNumber(totalObra)) * 100 : 0;

    // Debug log
    console.log('=== SERVIÇO: Cálculo Completo ===');
    console.log({
      totalObra: safeNumber(totalObra),
      custoDiretoTotal,
      custoEquipeTotal,
      custoFixosTotal,
      dasTotal,
      inssMensal,
      irrfMensal,
      darfTotal,
      totalGastos,
      lucroLiquido,
      lucroPercent
    });

    return {
      // Revenue
      totalObra,
      valorJaFaturado,
      valorAFaturar,
      
      // Direct costs
      custoEmpreiteiro,
      equipeMensalPrestadores,
      visitasObraTotal,
      outrosCustosDiretos,
      custoDiretoTotal,
      
      // Fixed costs
      fixosMensaisEscritorio,
      custoFixosTotal,
      
      // Team costs
      custoEquipeTotal,
      
      // Duration
      duracaoSemanas,
      duracaoMeses,
      
      // Taxes
      dasPercentualEfetivo,
      dasBaseCalculo,
      dasTotal,
      prolaboreMensal,
      inssPercentualSeg,
      inssMensal,
      irrfMensal,
      darfTotal,
      
      // Totals
      totalGastos,
      lucroLiquido,
      lucroPercent,
      
      // Metadata
      nomeObra,
      observacoes
    };
  },

  // Save calculation to database
  async saveCalculation(calculation) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    const { data, error } = await supabase?.from('profit_calculations')?.insert({
        user_id: user?.id,
        total_obra: calculation?.totalObra,
        valor_ja_faturado: calculation?.valorJaFaturado,
        valor_a_faturar: calculation?.valorAFaturar,
        custo_empreiteiro: calculation?.custoEmpreiteiro,
        equipe_mensal_prestadores: calculation?.equipeMensalPrestadores,
        visitas_obra_total: calculation?.visitasObraTotal,
        outros_custos_diretos: calculation?.outrosCustosDiretos,
        fixos_mensais_escritorio: calculation?.fixosMensaisEscritorio,
        duracao_semanas: calculation?.duracaoSemanas,
        duracao_meses: calculation?.duracaoMeses,
        das_percentual_efetivo: calculation?.dasPercentualEfetivo,
        das_base_calculo: calculation?.dasBaseCalculo,
        prolabore_mensal: calculation?.prolaboreMensal,
        inss_percentual_seg: calculation?.inssPercentualSeg,
        inss_mensal: calculation?.inssMensal,
        irrf_mensal: calculation?.irrfMensal,
        das_total: calculation?.dasTotal,
        darf_total: calculation?.darfTotal,
        custo_direto_total: calculation?.custoDiretoTotal,
        custo_equipe_total: calculation?.custoEquipeTotal,
        custo_fixos_total: calculation?.custoFixosTotal,
        total_gastos: calculation?.totalGastos,
        lucro_liquido: calculation?.lucroLiquido,
        lucro_percent: calculation?.lucroPercent,
        nome_obra: calculation?.nomeObra,
        observacoes: calculation?.observacoes
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  // Get all calculations (history)
  async getAllCalculations() {
    const { data, error } = await supabase?.from('profit_calculations')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(calc => ({
      id: calc?.id,
      createdAt: calc?.created_at,
      userId: calc?.user_id,
      totalObra: calc?.total_obra,
      valorJaFaturado: calc?.valor_ja_faturado,
      valorAFaturar: calc?.valor_a_faturar,
      custoEmpreiteiro: calc?.custo_empreiteiro,
      equipeMensalPrestadores: calc?.equipe_mensal_prestadores,
      visitasObraTotal: calc?.visitas_obra_total,
      outrosCustosDiretos: calc?.outros_custos_diretos,
      fixosMensaisEscritorio: calc?.fixos_mensais_escritorio,
      duracaoSemanas: calc?.duracao_semanas,
      duracaoMeses: calc?.duracao_meses,
      dasPercentualEfetivo: calc?.das_percentual_efetivo,
      dasBaseCalculo: calc?.das_base_calculo,
      prolaboreMensal: calc?.prolabore_mensal,
      inssPercentualSeg: calc?.inss_percentual_seg,
      inssMensal: calc?.inss_mensal,
      irrfMensal: calc?.irrf_mensal,
      dasTotal: calc?.das_total,
      darfTotal: calc?.darf_total,
      custoDiretoTotal: calc?.custo_direto_total,
      custoEquipeTotal: calc?.custo_equipe_total,
      custoFixosTotal: calc?.custo_fixos_total,
      totalGastos: calc?.total_gastos,
      lucroLiquido: calc?.lucro_liquido,
      lucroPercent: calc?.lucro_percent,
      nomeObra: calc?.nome_obra,
      observacoes: calc?.observacoes
    }));
  },

  // Get calculation by ID
  async getCalculationById(id) {
    const { data, error } = await supabase?.from('profit_calculations')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      createdAt: data?.created_at,
      userId: data?.user_id,
      totalObra: data?.total_obra,
      valorJaFaturado: data?.valor_ja_faturado,
      valorAFaturar: data?.valor_a_faturar,
      custoEmpreiteiro: data?.custo_empreiteiro,
      equipeMensalPrestadores: data?.equipe_mensal_prestadores,
      visitasObraTotal: data?.visitas_obra_total,
      outrosCustosDiretos: data?.outros_custos_diretos,
      fixosMensaisEscritorio: data?.fixos_mensais_escritorio,
      duracaoSemanas: data?.duracao_semanas,
      duracaoMeses: data?.duracao_meses,
      dasPercentualEfetivo: data?.das_percentual_efetivo,
      dasBaseCalculo: data?.das_base_calculo,
      prolaboreMensal: data?.prolabore_mensal,
      inssPercentualSeg: data?.inss_percentual_seg,
      inssMensal: data?.inss_mensal,
      irrfMensal: data?.irrf_mensal,
      dasTotal: data?.das_total,
      darfTotal: data?.darf_total,
      custoDiretoTotal: data?.custo_direto_total,
      custoEquipeTotal: data?.custo_equipe_total,
      custoFixosTotal: data?.custo_fixos_total,
      totalGastos: data?.total_gastos,
      lucroLiquido: data?.lucro_liquido,
      lucroPercent: data?.lucro_percent,
      nomeObra: data?.nome_obra,
      observacoes: data?.observacoes
    };
  },

  // Delete calculation
  async deleteCalculation(id) {
    const { error } = await supabase?.from('profit_calculations')?.delete()?.eq('id', id);
    
    if (error) throw error;
  }
};